"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  sessionItems,
  sessionStorageExpiring,
  setExternalSessionId,
} from "@/utils/sessionStorage";

interface SessionContextValue {
  sessionId: string;
  sessionKey: number;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface SessionProviderProps {
  children: ReactNode;
  externalSessionId?: string;
  sessionExpiredRedirectUrl?: string;
}

export function SessionProvider({ children, externalSessionId, sessionExpiredRedirectUrl }: SessionProviderProps) {
  const [sessionId, setSessionId] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    // Read the current session ID without mutating storage
    return sessionStorageExpiring.get<string>(sessionItems.SESSION_ID) ?? "";
  });
  const [sessionKey, setSessionKey] = useState(0);

  // After hydration, persist any external session ID and sync local state from storage
  useEffect(() => {
    if (externalSessionId) {
      setExternalSessionId(externalSessionId);
    }
    const stored = sessionStorageExpiring.get<string>(sessionItems.SESSION_ID);
    if (stored) {
      setSessionId(stored);
    }
  }, [externalSessionId]);

  // On session expiry: redirect if a URL is configured, otherwise get a fresh session ID and force remount.
  useEffect(() => {
    const onExpired = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === sessionItems.SESSION_ID) {
        if (sessionExpiredRedirectUrl) {
          window.location.href = sessionExpiredRedirectUrl;
        } else {
          const freshSessionId = sessionStorageExpiring.get<string>(sessionItems.SESSION_ID);
          if (freshSessionId) setSessionId(freshSessionId);
          setSessionKey((k) => k + 1);
        }
      }
    };

    window.addEventListener("sessionKeyExpired", onExpired);
    return () => window.removeEventListener("sessionKeyExpired", onExpired);
  }, [sessionExpiredRedirectUrl]);

  return (
    <SessionContext.Provider value={{ sessionId, sessionKey }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSessionId(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSessionId must be used within a SessionProvider");
  }
  return ctx;
}
